/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { cssMap, jsx } from '@compiled/react';

import { easeOut } from '@atlaskit/motion';
import { token } from '@atlaskit/tokens';
import { useIntl } from 'react-intl-next';
import messages from './messages';

import { type Organisation, type Product } from '../../../types';
import OrganisationItem from '../../primitives/organisation-item';
import ErrorBoundary from '../error-boundary';
import {
	NAVIGATION_CHANNEL,
	SWITCHER_ORG_ITEM,
	SWITCHER_ORG_LIST,
	UI_EVENT_TYPE,
	ViewedTracker,
} from '../../../common/utils/analytics';
import ResizeControl from './resize-control';
import { type AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	COLLAPSED_ORG_SELECTOR_WIDTH,
	DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH,
} from '../../../constants';
import { fg } from '@atlaskit/platform-feature-flags';

type Props = {
	activeOrgId: string;
	handleSetActiveOrg: (id: string) => void;
	organisations: Map<Organisation['id'], Organisation>;
	product: string | undefined;
	switchToSectionHeight?: number | null;
};

const styles = cssMap({
	orgList: {
		paddingTop: token('space.150'),
		paddingRight: 0,
		paddingBottom: token('space.150'),
		paddingLeft: 0,
		width: `56px`,
	},
	orgListTransition: {
		transition: `width 300ms ${easeOut} 0s`,
	},
	sidebar: {
		display: 'flex',
		flexDirection: 'row',
		overflowY: 'auto',
		borderRight: `1px solid ${token('color.border')}`,
	},
	orgItem: {
		marginTop: token('space.150'),
	},
	dragging: {
		cursor: 'ew-resize',
		// Make sure drag to resize does not animate as the user drags
		transition: 'none',
	},
});

const OrganisationSelector = ({
	activeOrgId,
	handleSetActiveOrg,
	organisations,
	product,
	switchToSectionHeight,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const orgItemBtnElements = useRef<HTMLButtonElement[]>([]);
	const orgListRef = useRef<HTMLDivElement | null>(null);

	const [isDragging, setIsDragging] = useState(false);
	const [percentageOpen, setPercentageOpen] = useState(0);
	const [resizeControlInitDone, setResizeControlInitDone] = useState(false);
	const [initialHeight, setInitialHeight] = useState(0);

	// The keyboard implementation is based on
	// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tablist_role#keyboard_interactions
	// https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-manual/
	const onKeyDownHandler = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
		let flag = false;
		switch (event.key) {
			// move focus to previous tab
			case 'ArrowUp':
				orgItemBtnElements.current[
					(orgItemBtnElements.current.length + (index - 1)) % orgItemBtnElements.current.length
				].focus();
				flag = true;
				break;

			// move focus to next tab
			case 'ArrowDown':
				orgItemBtnElements.current[(index + 1) % orgItemBtnElements.current.length].focus();
				flag = true;
				break;

			// move focus To the fist tab
			case 'Home':
				orgItemBtnElements.current[0].focus();
				flag = true;
				break;

			// move focus To the last tab
			case 'End':
				orgItemBtnElements.current[orgItemBtnElements.current.length - 1].focus();
				flag = true;
				break;

			default:
				break;
		}

		if (flag) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	const fireClickEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					eventType: UI_EVENT_TYPE,
					actionSubject: SWITCHER_ORG_ITEM,
					action: 'clicked',
					...payload,
				}).fire(NAVIGATION_CHANNEL);
			}
		},
		[createAnalyticsEvent],
	);

	// This will be used during resizing
	const updateOrgSelectorComponentWidth = useCallback((newWidth: number) => {
		setPercentageOpen(
			Math.min(
				1,
				(newWidth - COLLAPSED_ORG_SELECTOR_WIDTH) /
					(DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH - COLLAPSED_ORG_SELECTOR_WIDTH),
			),
		);
		orgListRef.current?.style.setProperty('width', `${newWidth}px`);

		// Ensure that the state is updated in the next tick so the transition style is applied after the width is updated
		setTimeout(() => {
			setResizeControlInitDone(true);
		}, 0);
	}, []);

	useEffect(() => {
		if (orgListRef.current?.scrollHeight) {
			setInitialHeight(orgListRef.current.scrollHeight);
		}
	}, []);

	if (organisations.size === 0) {
		return null;
	}

	const organisationsList = Array.from(organisations.entries());

	const isSwitcherDynamicHeight = fg('app_switcher_dynamic_height');

	return (
		<div css={styles.sidebar}>
			<ViewedTracker
				subject={SWITCHER_ORG_LIST}
				data={{
					numOrganisations: organisationsList.length,
				}}
			/>
			<div
				role="tablist"
				aria-label={formatMessage(messages.organisationsLabel)}
				aria-orientation="vertical"
				ref={orgListRef}
				css={[
					styles.orgList,
					resizeControlInitDone && styles.orgListTransition,
					isDragging && styles.dragging,
				]}
				style={{
					height:
						isSwitcherDynamicHeight && initialHeight > (switchToSectionHeight ?? 0)
							? `${switchToSectionHeight}px`
							: `${initialHeight}px`,
				}}
			>
				{organisationsList.map(([id, org], index) => (
					<ErrorBoundary
						key={id}
						product={product as Product}
						triggerSubject="orgItem"
						hideFallbackUI
					>
						<div css={index > 0 && styles.orgItem}>
							<OrganisationItem
								onKeyDown={(e) => {
									onKeyDownHandler(e, index);
								}}
								tabIndex={!activeOrgId || activeOrgId === id ? undefined : -1}
								isActive={activeOrgId === id}
								ref={(elem) => {
									if (elem) {
										orgItemBtnElements.current[index] = elem;
									}
								}}
								onClick={() => {
									fireClickEvent({
										attributes: {
											organisation: {
												id,
											},
										},
									});
									handleSetActiveOrg(id);
								}}
								percentageOpen={percentageOpen}
								{...org}
							/>
						</div>
					</ErrorBoundary>
				))}
			</div>
			<ResizeControl
				onDragStart={() => {
					setIsDragging(true);
				}}
				onDragEnd={() => {
					setIsDragging(false);
				}}
				updateOrgSelectorComponentWidth={updateOrgSelectorComponentWidth}
			/>
		</div>
	);
};

export default OrganisationSelector;
